const resources = {
  en: {
    gptAnalysis: {
      title: 'GPT Analysis',
      layout: {
        description: 'Generate tailored summaries, keep an eye on AI credits, and revisit saved analyses later.',
        runCardTitle: 'Run a new analysis',
        runCardDescription: 'Use the current selection to generate a fresh analysis without leaving the saved-history context.',
        runCardEmpty: 'Run an analysis to review the latest summary here.',
        anchorDayLabel: 'Anchor day',
        runButton: 'Run analysis',
        historyCardTitle: 'Saved analyses',
        historyCardDescription: 'Review past runs, open the full report, or remove analyses you no longer need.',
        emptyHistory: 'No saved analyses yet',
        viewDetails: 'View details',
        rerun: 'Rerun',
        deleteConfirm: 'Delete this analysis?',
        selectionSummary: '{{reportType}} · {{language}} · {{tone}}',
      },
      metrics: {
        savedAnalyses: 'Saved analyses',
        savedAnalysesMeta: 'Across your history',
        currentPage: 'Visible on this page',
        currentPageMeta: 'Page {{page}}',
        creditBalance: 'Credits',
        creditBalanceMeta: 'Available for future runs',
      },
      credits: {
        balance: 'Credits remaining: {{count}}',
        balanceUnknown: 'Credits balance unavailable',
        exhausted: 'Credits exhausted',
        resetOn: 'Credits reset on {{date}}',
      },
      notifications: {
        loadFailed: 'Failed to load GPT analysis',
        creditsExhausted: 'Credits exhausted. Please wait for the next refill or upgrade your plan.',
        deleteFailed: 'Failed to delete analysis',
        deleted: 'Analysis deleted',
        planRequired: 'Upgrade your plan to manage GPT analyses.',
      },
      history: {
        creditCost: 'Credit cost: {{count}}',
        loadFailed: 'Failed to load saved analyses',
      },
      options: {
        reportTypes: {
          general: 'General report',
          saving: 'Saving focus',
          earning: 'Earning focus',
          investment: 'Investment focus',
          high_risk: 'High-risk focus',
        },
        languages: {
          en: 'English',
          es: 'Spanish',
          ru: 'Russian',
          uk: 'Ukrainian',
        },
        tones: {
          insightful: 'Insightful & Analytical',
          concise: 'Concise & To the Point',
          motivational: 'Motivational & Supportive',
        },
      },
      sections: {
        summary: 'Summary',
        biggestRisks: 'Biggest risks',
        opportunities: 'Opportunities',
        nextActions: 'Next actions',
        assumptions: 'Assumptions / data used',
      },
      detail: {
        title: 'Analysis details',
        subtitle: 'Generated on {{date}}',
        back: 'Back to saved analyses',
        loadFailed: 'Failed to load analysis',
        reportCardTitle: '{{reportType}} report',
        reportCardDescription: 'Review the generated report together with its saved metadata and the follow-up AI conversation.',
        chatTitle: 'Conversation',
        chatDescription: 'Ask follow-up questions, export the thread, and keep the original report close by.',
        chatPlaceholder: 'Ask a follow-up question…',
        chatSend: 'Send',
        chatSending: 'Sending…',
        chatEmpty: 'No chat messages yet.',
        chatError: 'Failed to send the message.',
        providerUnavailableInline: 'AI provider is temporarily unavailable. We will retry shortly.',
        chatLoadError: 'Unable to load the chat history.',
        chatCredits: '{{count}} credits left',
        chatCreditsUnknown: 'Credits unavailable',
        chatCreditsEmpty: 'No AI credits left.',
        chatTyping: 'Typing…',
        chatCopy: 'Copy answer',
        chatCopySuccess: 'Answer copied.',
        chatCopyError: 'Unable to copy the answer.',
        export: {
          copy: 'Copy chat',
          download: 'Download .txt',
          copySuccess: 'Chat copied to clipboard.',
          copyError: 'Unable to copy the chat.',
          downloadError: 'Unable to download the chat.',
          copyHint: 'Paste into ChatGPT or notes.',
          downloadHint: 'Save a plain text file.',
          scrollToLatest: 'Jump to latest message',
          contextLabel: 'Context',
          userLabel: 'You',
          assistantLabel: 'AI',
        },
        meta: {
          created: 'Created',
          reportType: 'Report type',
          anchorDay: 'Anchor day',
          language: 'Language',
          tone: 'Style',
          credits: 'Credits',
        },
      },
    },
  },
  uk: {
    gptAnalysis: {
      title: 'GPT аналіз',
      layout: {
        description: 'Створюйте персоналізовані підсумки, стежте за балансом AI-кредитів і повертайтеся до збережених аналізів пізніше.',
        runCardTitle: 'Запустити новий аналіз',
        runCardDescription: 'Використайте поточний вибір, щоб згенерувати новий аналіз і не втрачати контекст збереженої історії.',
        runCardEmpty: 'Запустіть аналіз, щоб переглянути свіжий підсумок тут.',
        anchorDayLabel: 'Опорний день',
        runButton: 'Запустити аналіз',
        historyCardTitle: 'Збережені аналізи',
        historyCardDescription: 'Переглядайте попередні запуски, відкривайте повний звіт або видаляйте аналізи, які більше не потрібні.',
        emptyHistory: 'Поки що немає збережених аналізів',
        viewDetails: 'Переглянути деталі',
        rerun: 'Повторити',
        deleteConfirm: 'Видалити цей аналіз?',
        selectionSummary: '{{reportType}} · {{language}} · {{tone}}',
      },
      metrics: {
        savedAnalyses: 'Збережені аналізи',
        savedAnalysesMeta: 'У всій історії',
        currentPage: 'Видно на цій сторінці',
        currentPageMeta: 'Сторінка {{page}}',
        creditBalance: 'Кредити',
        creditBalanceMeta: 'Доступні для нових запусків',
      },
      credits: {
        balance: 'Залишок кредитів: {{count}}',
        balanceUnknown: 'Баланс кредитів недоступний',
        exhausted: 'Кредити вичерпані',
        resetOn: 'Кредити оновляться {{date}}',
      },
      notifications: {
        loadFailed: 'Не вдалося завантажити GPT-аналіз',
        creditsExhausted: 'Кредити вичерпані. Дочекайтеся наступного поповнення або підвищте тариф.',
        deleteFailed: 'Не вдалося видалити аналіз',
        deleted: 'Аналіз видалено',
        planRequired: 'Оновіть тариф, щоб керувати GPT-аналізами.',
      },
      history: {
        creditCost: 'Вартість у кредитах: {{count}}',
        loadFailed: 'Не вдалося завантажити збережені аналізи',
      },
      options: {
        reportTypes: {
          general: 'Загальний звіт',
          saving: 'Фокус на економії',
          earning: 'Фокус на заробітку',
          investment: 'Фокус на інвестиціях',
          high_risk: 'Фокус на високому ризику',
        },
        languages: {
          en: 'Англійська',
          es: 'Іспанська',
          ru: 'Російська',
          uk: 'Українська',
        },
        tones: {
          insightful: 'Аналітичний та глибокий',
          concise: 'Лаконічний та по суті',
          motivational: 'Підтримуючий та мотивуючий',
        },
      },
      sections: {
        summary: 'Підсумок',
        biggestRisks: 'Найбільші ризики',
        opportunities: 'Можливості',
        nextActions: 'Наступні дії',
        assumptions: 'Припущення / використані дані',
      },
      detail: {
        title: 'Деталі аналізу',
        subtitle: 'Створено {{date}}',
        back: 'Назад до збережених аналізів',
        loadFailed: 'Не вдалося завантажити аналіз',
        reportCardTitle: 'Звіт {{reportType}}',
        reportCardDescription: 'Переглядайте згенерований звіт разом із збереженими метаданими та подальшою AI-розмовою.',
        chatTitle: 'Розмова',
        chatDescription: 'Ставте уточнювальні запитання, експортуйте діалог і тримайте вихідний звіт поруч.',
        chatPlaceholder: 'Поставте уточнювальне запитання…',
        chatSend: 'Надіслати',
        chatSending: 'Надсилання…',
        chatEmpty: 'Повідомлень чату ще немає.',
        chatError: 'Не вдалося надіслати повідомлення.',
        providerUnavailableInline: 'AI-провайдер тимчасово недоступний. Ми спробуємо знову трохи пізніше.',
        chatLoadError: 'Не вдалося завантажити історію чату.',
        chatCredits: 'Залишилось кредитів: {{count}}',
        chatCreditsUnknown: 'Кредити недоступні',
        chatCreditsEmpty: 'AI-кредитів немає.',
        chatTyping: 'Друкує…',
        chatCopy: 'Скопіювати відповідь',
        chatCopySuccess: 'Відповідь скопійовано.',
        chatCopyError: 'Не вдалося скопіювати відповідь.',
        export: {
          copy: 'Скопіювати чат',
          download: 'Завантажити .txt',
          copySuccess: 'Чат скопійовано.',
          copyError: 'Не вдалося скопіювати чат.',
          downloadError: 'Не вдалося завантажити чат.',
          copyHint: 'Вставте у ChatGPT або нотатки.',
          downloadHint: 'Збережіть текстовий файл.',
          scrollToLatest: 'Перейти до останнього',
          contextLabel: 'Контекст',
          userLabel: 'Ви',
          assistantLabel: 'AI',
        },
        meta: {
          created: 'Створено',
          reportType: 'Тип звіту',
          anchorDay: 'Опорний день',
          language: 'Мова',
          tone: 'Стиль',
          credits: 'Кредити',
        },
      },
    },
  },
}

export default resources
